import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowRight, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";

const INTEREST_OPTIONS = [
  "Personal loan information",
  "Debt consolidation information",
  "Home improvement information",
  "Auto loan information",
  "Business loan information",
  "Mortgage information",
  "Student loan refinance information",
  "General inquiry",
];

const AMOUNT_RANGES = [
  "Under $10,000",
  "$10,000 – $25,000",
  "$25,000 – $50,000",
  "$50,000 – $100,000",
  "$100,000 – $250,000",
  "Over $250,000",
  "Not sure yet",
];

// US states + DC (kept short — informational only)
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

export default function LeadForm({ compact = false }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", state: "",
    interest: INTEREST_OPTIONS[0], amount_range: AMOUNT_RANGES[6],
    bank_name: "", bank_account_years: "",
    message: "",
  });
  const [contactConsent, setContactConsent] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    if (!privacyConsent) return toast.error("Please review and accept the Privacy notice to continue.");
    if (!contactConsent) return toast.error("Please provide consent to be contacted.");
    const years = Number(form.bank_account_years);
    if (!Number.isInteger(years) || years < 0) {
      return toast.error("Years of holding the bank account must be a positive whole number.");
    }
    setBusy(true);
    try {
      await api.post("/leads", { ...form, bank_account_years: years });
      setDone(true);
      toast.success("Thanks — we'll reach out shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="ef-card p-8 text-center" data-testid="lead-form-success">
        <div className="h-14 w-14 rounded-full bg-[#17A673]/15 border border-[#17A673]/50 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="h-6 w-6 text-[#17A673]" />
        </div>
        <div className="font-display text-2xl font-bold">Request received.</div>
        <p className="mt-2 text-white/60 text-sm max-w-md mx-auto">
          A member of our team will contact you within one business day at the email or phone number you provided. No further action is required from you right now.
        </p>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className={`ef-card p-6 md:p-7 ${compact ? "" : "shadow-2xl"}`}
      data-testid="lead-form"
    >
      <div className="mb-5">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#D4AF37]">Request Information</div>
        <div className="font-display text-xl font-bold mt-0.5">Talk with a lending advisor</div>
        <p className="text-xs text-white/55 mt-1.5">No credit check. We do not ask for your Social Security number, bank credentials, or account passwords on this form.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-white/50 font-mono">First Name</Label>
          <Input required data-testid="lead-first-name" value={form.first_name} onChange={(e) => set({ first_name: e.target.value })} className="mt-1.5 h-11 bg-white/5 border-white/10 text-white" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-white/50 font-mono">Last Name</Label>
          <Input required data-testid="lead-last-name" value={form.last_name} onChange={(e) => set({ last_name: e.target.value })} className="mt-1.5 h-11 bg-white/5 border-white/10 text-white" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-white/50 font-mono">Email</Label>
          <Input required type="email" data-testid="lead-email" value={form.email} onChange={(e) => set({ email: e.target.value })} className="mt-1.5 h-11 bg-white/5 border-white/10 text-white" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-white/50 font-mono">Phone</Label>
          <Input required type="tel" data-testid="lead-phone" placeholder="(786) 555 0100" value={form.phone} onChange={(e) => set({ phone: e.target.value })} className="mt-1.5 h-11 bg-white/5 border-white/10 text-white" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-white/50 font-mono">State of Residence</Label>
          <Select value={form.state} onValueChange={(v) => set({ state: v })}>
            <SelectTrigger data-testid="lead-state" className="mt-1.5 h-11 bg-white/5 border-white/10 text-white"><SelectValue placeholder="Select state" /></SelectTrigger>
            <SelectContent className="bg-[#0B1D3A] text-white border-white/10 max-h-64">
              {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-white/50 font-mono">Approx. Amount</Label>
          <Select value={form.amount_range} onValueChange={(v) => set({ amount_range: v })}>
            <SelectTrigger data-testid="lead-amount" className="mt-1.5 h-11 bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0B1D3A] text-white border-white/10">
              {AMOUNT_RANGES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[10px] uppercase tracking-widest text-white/50 font-mono">I'm interested in</Label>
          <Select value={form.interest} onValueChange={(v) => set({ interest: v })}>
            <SelectTrigger data-testid="lead-interest" className="mt-1.5 h-11 bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0B1D3A] text-white border-white/10">
              {INTEREST_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-white/50 font-mono">Bank Name</Label>
          <Input
            required
            data-testid="lead-bank-name"
            placeholder="Enter your bank name"
            value={form.bank_name}
            onChange={(e) => set({ bank_name: e.target.value })}
            className="mt-1.5 h-11 bg-white/5 border-white/10 text-white"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-white/50 font-mono">Years Holding Bank Account</Label>
          <Input
            required
            type="number"
            min="0"
            step="1"
            inputMode="numeric"
            data-testid="lead-bank-account-years"
            placeholder="Enter the number of years"
            value={form.bank_account_years}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "" || /^\d+$/.test(v)) set({ bank_account_years: v });
            }}
            className="mt-1.5 h-11 bg-white/5 border-white/10 text-white"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[10px] uppercase tracking-widest text-white/50 font-mono">Anything you'd like us to know (optional)</Label>
          <Textarea data-testid="lead-message" rows={3} value={form.message} onChange={(e) => set({ message: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white" />
        </div>
      </div>

      {/* Consents */}
      <div className="mt-6 space-y-3">
        <label className="flex items-start gap-2.5 text-[11px] text-white/70 leading-relaxed cursor-pointer">
          <Checkbox checked={contactConsent} onCheckedChange={setContactConsent} data-testid="lead-contact-consent" className="border-white/25 mt-0.5" />
          <span>
            By checking this box, I authorize Elevate Financial and its lending partners to contact me at the email address and phone number I provided, including by autodialed and pre-recorded phone calls and SMS text messages. Message and data rates may apply. Consent is <strong>not</strong> a condition of receiving any product or service. Reply STOP to opt out at any time.
          </span>
        </label>
        <label className="flex items-start gap-2.5 text-[11px] text-white/70 leading-relaxed cursor-pointer">
          <Checkbox checked={privacyConsent} onCheckedChange={setPrivacyConsent} data-testid="lead-privacy-consent" className="border-white/25 mt-0.5" />
          <span>
            I have read and agree to the Privacy Notice. I understand my information may be shared with third-party lending partners who can help match me with credit products.
          </span>
        </label>
      </div>

      <Button
        type="submit"
        disabled={busy}
        data-testid="lead-submit"
        className="ef-btn-primary w-full rounded-full h-12 mt-6 text-sm font-medium"
      >
        {busy ? "Submitting..." : <>Request Information <ArrowRight className="h-4 w-4 ml-2" /></>}
      </Button>

      <div className="mt-4 flex items-start gap-2 text-[10px] text-white/40 leading-relaxed">
        <ShieldCheck className="h-3 w-3 mt-0.5 shrink-0 text-[#17A673]" />
        <span>
          Elevate Financial is not a bank and is not FDIC-insured. This is an initial information request only — not a credit application, credit offer, or commitment to lend. Rates and terms are provided by third-party partners subject to their own underwriting.
        </span>
      </div>
    </form>
  );
}
